<template>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Loading & Error -->
    <div v-if="isLoading && !survivors.length" class="flex flex-col items-center py-10">
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600 mb-3"></div>
      <p class="text-indigo-200 text-sm">กำลังโหลดอันดับ...</p>
    </div>
    <div v-else-if="error" class="text-center text-red-300 py-10 text-sm">
      {{ error }}
    </div>

    <!-- รายการผู้รอดชีวิต -->
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
      <div
        v-for="(survivor, index) in survivors"
        :key="survivor.docId"
        class="bg-gray-900 bg-opacity-70 rounded-md p-4 flex items-center gap-4 border border-gray-800 transition-all duration-200 cursor-pointer"
        :class="[survivor.docId === userId ? 'border-2 border-indigo-400 cursor-default' : ' ']"
        @click="openCharacterView(survivor.docId)"
      >
        <!-- อันดับ -->
        <div class="w-10 h-10 flex items-center justify-center rounded-full bg-indigo-600 text-white font-semibold text-base border border-indigo-400">
          #{{ index + 1 }}
        </div>

        <!-- รูปโปรไฟล์ -->
        <div class="relative">
          <img
            :src="survivor.imageUrl || 'https://via.placeholder.com/64'"
            alt="Survivor"
            class="w-14 h-14 rounded-md object-cover border-2 border-indigo-400"
            @error="handleImageError"
          />
          <div class="absolute -bottom-1 -right-1 bg-indigo-600 text-white text-xs font-semibold rounded-full w-5 h-5 flex items-center justify-center border border-gray-600">
            {{ survivor.level || 'N/A' }}
          </div>
        </div>

        <!-- ข้อมูลผู้รอดชีวิต -->
        <div class="flex-1">
          <h3 class="text-indigo-200 text-sm font-semibold truncate">{{ survivor.characterName || 'Unknown' }}</h3>
          <div class="flex flex-col gap-1 text-xs text-indigo-200">
            <span><i class="fas fa-door-closed mr-1"></i>ห้อง: {{ survivor.room }}</span>
            <span><i class="fas fa-clock mr-1"></i>{{ formatSurvivalTime(survivor.survivalStartTime) }}</span>
          </div>
        </div>
      </div>

      <!-- ถ้าไม่มีผู้รอดชีวิต -->
      <div v-if="survivors.length === 0" class="text-center text-indigo-200 py-10 text-sm">
        ไม่พบผู้รอดชีวิตที่มีมากกว่าหรือเท่ากับ 8 ห้อง
      </div>
    </div>

    <!-- Load More Button -->
    <div v-if="hasMore && !isLoading" class="flex justify-center mt-6">
      <button
        @click="loadMore"
        class="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 transition-colors duration-200 text-sm font-semibold"
      >
        โหลดเพิ่ม
      </button>
    </div>

    <!-- Loading indicator for Load More -->
    <div v-if="isLoading && survivors.length" class="flex justify-center mt-6">
      <div class="animate-spin rounded-full h-8 w-8 border-t-4 border-indigo-600"></div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { projectFirestore, projectAuth } from '@/firebase/config';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);
dayjs.locale('th');

export default {
  setup() {
    const survivors = ref([]);
    const isLoading = ref(true);
    const error = ref(null);
    const userId = ref(null);
    const showRewardModal = ref(false);
    const router = useRouter();
    const lastDoc = ref(null); // Track the last document for pagination
    const hasMore = ref(true); // Track if more data is available
    const pageSize = 50; // Number of survivors to fetch per batch
    let unsubscribe = null;

    // Handle image load errors
    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/64';
    };

    // Fetch userId of the player
    const fetchUserId = async () => {
      try {
        const user = projectAuth.currentUser;
        if (user) {
          userId.value = user.uid;
        } else {
          error.value = 'กรุณาล็อกอินเพื่อดูข้อมูล';
        }
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการดึงข้อมูลผู้ใช้: ' + err.message;
      }
    };

    // Fetch survivor data
    const fetchSurvivors = async (isLoadMore = false) => {
      try {
        isLoading.value = true;
        let query = projectFirestore
          .collection('characters')
          .where('isLive', '==', true)
          .orderBy('level', 'desc')
          .orderBy('room','desc')
          .limit(pageSize);

        // If loading more, start after the last document
        if (isLoadMore && lastDoc.value) {
          query = query.startAfter(lastDoc.value);
        }

        const snapshot = await query.get();
        const newSurvivors = snapshot.docs.map((doc) => ({
          docId: doc.id,
          ...doc.data(),
        }));

        // Append new survivors if loading more, otherwise replace
        if (isLoadMore) {
          survivors.value = [...survivors.value, ...newSurvivors];
        } else {
          survivors.value = newSurvivors;
        }

        // Update last document and hasMore flag
        lastDoc.value = snapshot.docs[snapshot.docs.length - 1] || null;
        hasMore.value = snapshot.docs.length === pageSize;

        isLoading.value = false;
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + err.message;
        isLoading.value = false;
      }
    };

    // Load more survivors
    const loadMore = () => {
      if (hasMore.value && !isLoading.value) {
        fetchSurvivors(true);
      }
    };

    // Format survival time with dayjs
    const formatSurvivalTime = (timestamp) => {
      if (!timestamp || !timestamp.toDate) return 'ไม่ทราบ';
      try {
        return `รอดมา: ${dayjs(timestamp.toDate()).fromNow()}`;
      } catch {
        return 'ไม่ทราบ';
      }
    };

    // Open AdminCharacterView in a new tab
    const openCharacterView = (docId) => {
      const routeUrl = router.resolve({
        name: 'AdminCharacterView',
        params: { userId: docId },
      }).href;
      window.open(routeUrl, '_blank');
    };

    onMounted(async () => {
      await fetchUserId();
      await fetchSurvivors();
    });

    onUnmounted(() => {
      if (unsubscribe) {
        unsubscribe();
      }
    });

    return {
      survivors,
      isLoading,
      error,
      formatSurvivalTime,
      userId,
      handleImageError,
      showRewardModal,
      openCharacterView,
      loadMore, // Expose loadMore method
      hasMore, // Expose hasMore flag
    };
  },
};
</script>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 5px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2FGuestroom.png?alt=media&token=fc8f6e11-d047-4811-a36a-e7f225ef9d27') no-repeat center center fixed,
    linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
}
</style>